<!-- src/apps/workspace/layouts/SettingsLayout.vue -->

<!--
  Settings Layout for workspace account settings pages.
  Provides horizontal tab navigation + content area.
  NOTE: This component does NOT include header/footer - those come from
  the route's meta.layout (WorkspaceLayout) via App.vue.
-->

<script setup lang="ts">
import { getSettingsNavigationSections } from '@/apps/workspace/config/settings-navigation';
import OIcon from '@/shared/components/icons/OIcon.vue';
import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
import { debugLog } from '@/utils/debug';
import {
  hasPasswordOf,
  isFullAuthModeOf,
  isOwnerOrAdminOf,
  isSsoEnabledOf,
  isSsoOnlyModeOf,
  isWebAuthnEnabledOf,
} from '@/utils/features';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const bootstrapStore = useBootstrapStore();

/**
 * Tabs suppressed on this deployment.
 *
 * SeuID runs a SINGLE region, so the "Região" tab offers a choice that does
 * not exist — the Figma Make "Conta" mock shows it, and the product owner
 * ruled it out for exactly that reason. Filtered here, at the presentation
 * layer, rather than in settings-navigation.ts: that module encodes upstream's
 * role-visibility matrix (owner/admin only) which the shared config tests
 * assert, and the /account/region* routes stay reachable by direct link for
 * whenever multi-region becomes real.
 */
const HIDDEN_TAB_IDS = new Set(['region']);

// Flatten navigation sections into tab items.
//
// Reactivity: deriving feature flags from `bootstrapStore` inside the computed
// registers each accessed field as a dependency. When checkWindowStatus or
// changePassword updates the store (e.g. has_password flips after first
// password set), this recomputes and tabs appear without a page reload.
const tabItems = computed(() => {
  const features = {
    hasPassword: hasPasswordOf(bootstrapStore),
    isFullAuthMode: isFullAuthModeOf(bootstrapStore),
    isSsoOnlyMode: isSsoOnlyModeOf(bootstrapStore),
    isOwnerOrAdmin: isOwnerOrAdminOf(bootstrapStore),
    isWebAuthnEnabled: isWebAuthnEnabledOf(bootstrapStore),
    isSsoEnabled: isSsoEnabledOf(bootstrapStore),
  };
  const sections = getSettingsNavigationSections(t, features);
  const allItems = sections
    .flatMap((section) => section.items)
    .filter((item) => !HIDDEN_TAB_IDS.has(item.id));
  const visibleItems = allItems.filter((item) => (item.visible ? item.visible() : true));
  debugLog.features('SettingsLayout.tabItems', {
    features,
    allItems: allItems.map(i => i.id),
    visibleItems: visibleItems.map(i => i.id),
  });
  return visibleItems;
});

// Check if route matches item or any of its children
const isActiveRoute = (item: (typeof tabItems.value)[0]): boolean => {
  if (route.path === item.to || route.path.startsWith(item.to + '/')) return true;
  if (item.children) {
    return item.children.some(
      (child) => route.path === child.to || route.path.startsWith(child.to + '/')
    );
  }
  return false;
};
</script>

<template>
  <!--
    The mock centres the account surface in an 800px column, narrower than the
    max-w-5xl this used, so the cards stop stretching into banners.

    `font-sans` here is load-bearing, not decorative: the base layer sets
    `html { font-family: ui-serif … }`, so anything that does not name a family
    renders in Georgia — measured on the real page, the whole tab bar and every
    card body did. The mock's account surface is the body sans (Outfit)
    throughout, with the serif reserved for the "Conta" title and the mono for
    identifiers; declaring it once here lets the panels inherit it.
  -->
  <div class="mx-auto max-w-[800px] px-4 pb-24 pt-8 sm:px-6">
    <!-- Back to Dashboard -->
    <!-- prettier-ignore-attribute class -->
    <router-link
      to="/"
      class="
        group mb-5 inline-flex items-center gap-2 text-sm text-day-ink-muted transition-colors
        hover:text-day-ink dark:text-night-ink-muted dark:hover:text-night-ink">
      <OIcon
        collection="heroicons"
        name="arrow-left"
        class="size-4 transition-transform group-hover:-translate-x-0.5"
        aria-hidden="true" />
      {{ t('web.settings.back_to_dashboard') }}
    </router-link>

    <!-- Page Header: the mock sets "Conta" in the serif at 30px/400 — the
         brand slab (font-brand = Zilla Slab), not the body sans. -->
    <div class="mb-7">
      <h1 class="font-brand text-[30px] font-normal leading-tight text-day-ink dark:text-night-ink">
        {{ t('web.TITLES.account') }}
      </h1>
      <p class="mt-1.5 text-[15px] text-day-ink-faint dark:text-night-ink-faint">
        {{ t('web.settings.manage_your_account_settings_and_preferences') }}
      </p>
    </div>

    <!-- Tab Navigation -->
    <nav
      class="-mb-px flex gap-1 overflow-x-auto border-b border-day-line dark:border-white/[0.13]"
      aria-label="Settings navigation">
      <router-link
        v-for="item in tabItems"
        :key="item.id"
        :to="item.to"
        :class="[
          'flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm transition-colors',
          isActiveRoute(item)
            ? 'border-brand-600 font-semibold text-brand-600 dark:border-brand-500 dark:text-brand-500'
            : 'border-transparent font-normal text-day-ink-faint hover:text-day-ink-muted dark:text-night-ink-faint dark:hover:text-night-ink-muted',
        ]">
        <OIcon
          :collection="item.icon.collection"
          :name="item.icon.name"
          class="size-[15px] shrink-0"
          aria-hidden="true" />
        {{ item.label }}
      </router-link>
    </nav>

    <!-- Content Area -->
    <div class="pt-8">
      <slot></slot>
    </div>
  </div>
</template>
